import { useState, useEffect } from 'react';
import { Loader2, AlertTriangle, RefreshCw } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { hostawayAPI } from '../../lib/api';
import { toast } from 'sonner';
import { formatPropertyLabel } from '../../lib/propertyLabel';

export function HostawayDiagnosticsPanel() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [dedupRunning, setDedupRunning] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: d } = await hostawayAPI.diagnostics();
      setData(d);
    } catch (err) {
      setError(err.message || 'Failed to fetch');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDedup = async () => {
    setDedupRunning(true);
    try {
      const res = await hostawayAPI.dedup();
      toast.success(`Dedup complete: ${res.data.total_superseded || 0} Smoobu bookings superseded`);
      await fetchData();
    } catch (err) {
      toast.error(err.message || 'Dedup failed');
    } finally {
      setDedupRunning(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Hostaway Diagnostics</CardTitle>
          <Button variant="ghost" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading...
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-500">
            <AlertTriangle className="h-4 w-4" />
            {error}
          </div>
        )}
        {data && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <p className="font-medium text-lg tabular-nums">{data.listings?.total ?? 0}</p>
                <p className="text-xs text-muted-foreground">Properties</p>
              </div>
              <div>
                <p className="font-medium text-lg tabular-nums">{data.hostaway?.total ?? 0}</p>
                <p className="text-xs text-muted-foreground">Reservations</p>
              </div>
              <div>
                <p className="font-medium text-lg tabular-nums">{data.listings?.mapped ?? 0}</p>
                <p className="text-xs text-muted-foreground">Listing Mappings</p>
              </div>
              <div>
                <p className="font-medium text-lg tabular-nums">{data.smoobu?.superseded ?? 0}</p>
                <p className="text-xs text-muted-foreground">Smoobu Superseded</p>
              </div>
            </div>

            {data.last_sync?.hostaway?.started_at && (
              <p className="text-xs text-muted-foreground">
                Last sync: {new Date(data.last_sync.hostaway.started_at).toLocaleString()}
              </p>
            )}

            {data.connection?.connected === false && (
              <div className="flex items-center gap-2 text-sm text-amber-600">
                <AlertTriangle className="h-4 w-4" />
                Hostaway credentials not configured. Set HOSTAWAY_ACCOUNT_ID and HOSTAWAY_API_KEY in environment, or save via the Integrations page.
              </div>
            )}

            {data.ownership?.skip_reservation_count > 0 && (
              <div>
                <h4 className="text-sm font-medium mb-2 flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  Reservations skipped ({data.ownership.skip_reservation_count}) &mdash; not-owned listings
                </h4>
                <p className="text-xs text-muted-foreground mb-2">
                  Ingested reservations on a Hostaway listing not marked owned are skipped, not
                  written. Mark the listing owned via Configure if it should be counted.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/30">
                        <th className="text-left px-3 py-2 font-medium">Listing</th>
                        <th className="text-right px-3 py-2 font-medium">Reservations skipped</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data.ownership.skip_sample || []).map((s) => (
                        <tr key={s.listing_id} className="border-b">
                          <td className="px-3 py-2 text-xs">
                            {formatPropertyLabel(s.listing_name, s.listing_id)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {s.skipped_reservation_count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.ownership.skip_listing_count > (data.ownership.skip_sample || []).length && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {`Showing first ${(data.ownership.skip_sample || []).length} of ${data.ownership.skip_listing_count} listings.`}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={handleDedup} disabled={dedupRunning}>
                {dedupRunning ? 'Running Dedup...' : 'Run Dedup'}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
